// Secure credential hand-off from a trusted dashboard via window.postMessage.
//
// This client is static and client-side: to log into a peer it must compute
// sha256(sha256(pw+salt)+challenge), so the browser necessarily holds the
// plaintext password in memory. There is no way around that for a client-side
// login. What we CAN control is the transport — and postMessage keeps the
// password out of the URL, browser history, referrers, server logs, and
// localStorage entirely.
//
// Flow (the dashboard opens this client with window.open() and keeps the handle):
//   1. client -> opener   { type:"rustdesk-web:ready",   nonce }         targetOrigin "*"
//        (carries no secret; only the real opener window receives it)
//   2. opener -> client   { type:"rustdesk-web:connect", nonce, id, password, ... }
//        targetOrigin = this client's exact origin (the dashboard knows it)
//   3. client accepts only if event.origin is allow-listed AND the nonce matches,
//        then auto-connects. The password is used once and never persisted.

export interface HandoffPayload {
  id: string;
  password?: string;
  // Optional: let the dashboard fully provision the server too. Omitted fields
  // fall back to the client's saved settings.
  server?: string;
  relay?: string;
  key?: string;
  schema?: "wss://" | "ws://";
}

const READY = "rustdesk-web:ready";
const CONNECT = "rustdesk-web:connect";

/**
 * Origins allowed to send credentials. Set this to your dashboard's origin(s).
 * Configure via `window.RUSTDESK_TRUSTED_ORIGINS` (a string or string[]), e.g.
 * baked into index.html:  window.RUSTDESK_TRUSTED_ORIGINS = "https://dash.example.com";
 * If left unset, the hand-off still requires the secret nonce, but you lose the
 * defense-in-depth of pinning the sender — so setting it is strongly recommended.
 */
function trustedOrigins(): string[] {
  const w = (window as unknown as { RUSTDESK_TRUSTED_ORIGINS?: unknown }).RUSTDESK_TRUSTED_ORIGINS;
  if (Array.isArray(w)) return w.map(String);
  if (typeof w === "string") return w.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
}

function makeNonce(): string {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * If this page was opened by another window, perform the ready/connect handshake
 * and invoke `onConnect` with the credentials once a trusted, nonce-matching
 * message arrives. No-op when there is no opener (normal manual use).
 */
export function initHandoff(onConnect: (p: HandoffPayload) => void): void {
  if (!window.opener || window.opener.closed) return; // only when launched by a parent window

  const nonce = makeNonce();
  const allow = trustedOrigins();
  let done = false;

  const stopPinging = () => clearInterval(pingTimer);

  window.addEventListener("message", (e: MessageEvent) => {
    const data = e.data as { type?: string; nonce?: string } & Partial<HandoffPayload>;
    if (!data || data.type !== CONNECT || done) return;

    if (allow.length > 0 && !allow.includes(e.origin)) {
      console.warn("[handoff] rejected credentials from untrusted origin:", e.origin);
      return;
    }
    if (!allow.length) {
      console.warn(
        "[handoff] RUSTDESK_TRUSTED_ORIGINS is not set — accepting nonce-matched message from",
        e.origin,
        "(set the allowlist for defense-in-depth)"
      );
    }
    if (data.nonce !== nonce) {
      console.warn("[handoff] nonce mismatch — ignoring message");
      return;
    }

    const id = String(data.id ?? "").replace(/\s/g, "");
    if (!id) return;

    done = true;
    stopPinging();
    onConnect({
      id,
      password: typeof data.password === "string" ? data.password : undefined,
      server: typeof data.server === "string" ? data.server : undefined,
      relay: typeof data.relay === "string" ? data.relay : undefined,
      key: typeof data.key === "string" ? data.key : undefined,
      schema: data.schema === "ws://" ? "ws://" : data.schema === "wss://" ? "wss://" : undefined,
    });
  });

  // Announce readiness to the opener, retrying briefly in case the dashboard
  // attaches its listener a moment after window.open().
  let tries = 0;
  const ping = () => {
    if (done || tries++ > 40) return stopPinging(); // ~6s ceiling
    try {
      window.opener?.postMessage({ type: READY, nonce }, "*");
    } catch {
      stopPinging();
    }
  };
  const pingTimer = setInterval(ping, 150);
  ping();
}
